import React, { useState, useEffect } from 'react';
import { getAvailableSeasons, SEASONS, getCurrentSeason } from '../data/seasons';

// Helper to format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

// Helper to get today's date
const todayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Helper to format date for input
const formatDateForInput = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const SeasonFilter = ({
  selectedSeason,
  onSeasonChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onFilterChange // Callback when any filter changes
}) => {
  const [datePreset, setDatePreset] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);

  // Initialize with current season and set dates
  useEffect(() => {
    if (!selectedSeason) {
      const current = getCurrentSeason();
      onSeasonChange(current);
    } else if (selectedSeason && SEASONS[selectedSeason]) {
      // Ensure dates are set when season is selected
      const season = SEASONS[selectedSeason];
      if (!startDate || !endDate) {
        onStartDateChange(season.startDate);
        onEndDateChange(season.endDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply date preset within selected season
  const applyDatePreset = (preset) => {
    if (!selectedSeason || !SEASONS[selectedSeason]) {
      // No season selected, use normal date presets
      const today = new Date();
      const end = todayLocal();
      let start = '';

      switch (preset) {
        case 'last7':
          const last7 = new Date(today);
          last7.setDate(today.getDate() - 7);
          start = formatDateForInput(last7);
          break;
        case 'last30':
          const last30 = new Date(today);
          last30.setDate(today.getDate() - 30);
          start = formatDateForInput(last30);
          break;
        case 'thisMonth':
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          start = formatDateForInput(firstDay);
          break;
        case 'lastMonth':
          const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          start = formatDateForInput(firstDayLastMonth);
          onStartDateChange(start);
          onEndDateChange(formatDateForInput(lastDayLastMonth));
          return;
        case 'all':
          onStartDateChange('');
          onEndDateChange('');
          return;
        default:
          return;
      }
      onStartDateChange(start);
      onEndDateChange(end);
      return;
    }

    // Season is selected - apply preset within season boundaries
    const season = SEASONS[selectedSeason];
    const today = new Date();
    const todayStr = todayLocal();

    let start = season.startDate;
    let end = season.endDate;

    switch (preset) {
      case 'fullSeason':
        // Full season range
        start = season.startDate;
        end = season.endDate;
        break;
      case 'last7':
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        const last7Str = formatDateForInput(last7);
        start = last7Str > season.startDate ? last7Str : season.startDate;
        end = todayStr < season.endDate ? todayStr : season.endDate;
        break;
      case 'last30':
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 30);
        const last30Str = formatDateForInput(last30);
        start = last30Str > season.startDate ? last30Str : season.startDate;
        end = todayStr < season.endDate ? todayStr : season.endDate;
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const firstDayStr = formatDateForInput(firstDay);
        start = firstDayStr > season.startDate ? firstDayStr : season.startDate;
        end = todayStr < season.endDate ? todayStr : season.endDate;
        break;
      case 'lastMonth':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        const firstDayLastMonthStr = formatDateForInput(firstDayLastMonth);
        const lastDayLastMonthStr = formatDateForInput(lastDayLastMonth);
        start = firstDayLastMonthStr > season.startDate ? firstDayLastMonthStr : season.startDate;
        end = lastDayLastMonthStr < season.endDate ? lastDayLastMonthStr : season.endDate;
        break;
      case 'custom':
        setShowCustomDates(true);
        return;
      case 'all':
        start = season.startDate;
        end = season.endDate;
        break;
      default:
        return;
    }

    onStartDateChange(start);
    onEndDateChange(end);
    setShowCustomDates(false);
  };

  // When season changes, reset to full season range
  useEffect(() => {
    if (selectedSeason && SEASONS[selectedSeason]) {
      const season = SEASONS[selectedSeason];
      // Only reset if dates are not already set or are outside season range
      if (!startDate || !endDate || startDate < season.startDate || endDate > season.endDate) {
        onStartDateChange(season.startDate);
        onEndDateChange(season.endDate);
        setDatePreset('fullSeason');
        setShowCustomDates(false);
        if (onFilterChange) onFilterChange();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason]);

  // Get available date presets based on whether season is selected
  const getDatePresets = () => {
    if (!selectedSeason || !SEASONS[selectedSeason]) {
      return [
        { id: 'last7', label: 'Last 7 Days' },
        { id: 'last30', label: 'Last 30 Days' },
        { id: 'thisMonth', label: 'This Month' },
        { id: 'lastMonth', label: 'Last Month' },
        { id: 'all', label: 'All Time' },
        { id: 'custom', label: 'Custom Range' }
      ];
    }

    const season = SEASONS[selectedSeason];
    return [
      { id: 'fullSeason', label: `Full ${season.name}` },
      { id: 'last7', label: 'Last 7 Days' },
      { id: 'last30', label: 'Last 30 Days' },
      { id: 'thisMonth', label: 'This Month' },
      { id: 'lastMonth', label: 'Last Month' },
      { id: 'custom', label: 'Custom Range' }
    ];
  };

  const handleSeasonChange = (season) => {
    onSeasonChange(season);
    if (onFilterChange) onFilterChange();
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    applyDatePreset(preset);
    if (onFilterChange) onFilterChange();
  };

  const handleCustomDateChange = () => {
    setDatePreset('custom');
    setShowCustomDates(true);
    if (onFilterChange) onFilterChange();
  };

  // Get min/max dates for custom date inputs (constrained to season)
  const getDateConstraints = () => {
    if (selectedSeason && SEASONS[selectedSeason]) {
      const season = SEASONS[selectedSeason];
      return {
        min: season.startDate,
        max: Math.min(season.endDate, todayLocal())
      };
    }
    return {
      min: '',
      max: todayLocal()
    };
  };

  const dateConstraints = getDateConstraints();

  return (
    <>
      {/* Season Selector - Primary Filter */}
      <div>
        <label>Season</label>
        <select
          value={selectedSeason || ''}
          onChange={e => handleSeasonChange(e.target.value)}
        >
          <option value="">All Seasons</option>
          {getAvailableSeasons().map(seasonKey => (
            <option key={seasonKey} value={seasonKey}>
              {SEASONS[seasonKey].name} {SEASONS[seasonKey].isActive ? '(Current)' : ''}
            </option>
          ))}
        </select>
        {selectedSeason && SEASONS[selectedSeason] && (
          <p style={{ marginTop: '4px', fontSize: '11px', color: '#888' }}>
            {formatDateDisplay(SEASONS[selectedSeason].startDate)} - {formatDateDisplay(SEASONS[selectedSeason].endDate)}
          </p>
        )}
      </div>

      {/* Date Range - Secondary Filter (within season) */}
      {selectedSeason && (
        <>
          <div>
            <label>Date Range</label>
            <select
              value={datePreset}
              onChange={e => handleDatePresetChange(e.target.value)}
            >
              {getDatePresets().map(preset => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
            {/* Show current date range when not custom */}
            {startDate && endDate && !showCustomDates && (
              <p style={{ marginTop: '4px', fontSize: '11px', color: '#888' }}>
                {formatDateDisplay(startDate)} - {formatDateDisplay(endDate)}
              </p>
            )}
          </div>

          {/* Custom Date Inputs - As separate filter items */}
          {showCustomDates && (
            <>
              <div>
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => {
                    const value = e.target.value;
                    onStartDateChange(value);
                    handleCustomDateChange();
                  }}
                  min={dateConstraints.min}
                  max={dateConstraints.max}
                />
              </div>
              <div>
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => {
                    const value = e.target.value;
                    onEndDateChange(value);
                    handleCustomDateChange();
                  }}
                  min={startDate || dateConstraints.min}
                  max={dateConstraints.max}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Show date inputs when no season selected */}
      {!selectedSeason && (
        <>
          <div>
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => {
                const value = e.target.value;
                onStartDateChange(value);
                if (onFilterChange) onFilterChange();
              }}
              max={endDate || todayLocal()}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => {
                const value = e.target.value;
                onEndDateChange(value);
                if (onFilterChange) onFilterChange();
              }}
              max={todayLocal()}
            />
          </div>
        </>
      )}
    </>
  );
};

export default SeasonFilter;

